import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  ArrowLeft, ChevronDown, ChevronUp, Copy, Eye, GripVertical, History,
  ImageOff, Link as LinkIcon, Loader2, MoreVertical, Package, Plus, RotateCcw, Trash2, Upload,
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Hero from '@/components/commerce/Hero';
import ShopFooter from '@/components/commerce/ShopFooter';
import { FaqSection, ImageTextSection, NewsletterSection } from '@/components/commerce/ContentSections';
import PriceTag from '@/components/commerce/PriceTag';
import { getCategoryIcon } from '@/components/commerce/categoryIcon';
import { resolveCommerceImageUrl } from '@/lib/media';
import { deriveProductCollections } from '@/lib/commerceProducts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (isoString) => {
  if (!isoString) return null;
  try {
    return formatDistanceToNow(new Date(isoString), { addSuffix: true, locale: nl });
  } catch (error) {
    return null;
  }
};

const SECTION_LABELS = {
  hero: 'Hero',
  categories: 'Categorieën',
  featured_products: 'Uitgelicht',
  new_products: 'Nieuw binnen',
  bestseller_products: 'Meest verkocht',
  deals_products: 'Acties',
  image_text: 'Afbeelding met tekst',
  faq: 'Veelgestelde vragen',
  newsletter: 'Nieuwsbrief',
};

// Product/category rows pull real data instead of a static mockup -- see
// PreviewProductGrid/PreviewCategoriesGrid below.
const PRODUCT_ROW_TYPES = ['featured_products', 'new_products', 'bestseller_products', 'deals_products'];

const PREVIEW_EMPTY_COPY = {
  categories: 'Voeg categorieën toe om ze hier te tonen.',
  featured_products: 'Deze sectie toont automatisch je producten zodra je ze toevoegt.',
  new_products: 'Deze sectie toont automatisch je nieuwste producten zodra je ze toevoegt.',
  bestseller_products: 'Deze sectie vult zich zodra klanten producten bestellen.',
  deals_products: 'Deze sectie toont producten zodra je een actieprijs instelt.',
};

// Mirrors backend/commerce_theme.py's SECTION_TYPE_REGISTRY /
// SECTION_TYPE_DEFAULT_SETTINGS -- drives the "Sectie toevoegen" picker
// (which types exist, whether the max count is already reached) and seeds
// sensible defaults for a freshly added section.
const ADDABLE_SECTION_TYPES = [
  { type: 'hero', maxCount: 1 },
  { type: 'categories', maxCount: 1 },
  { type: 'featured_products', maxCount: null },
  { type: 'new_products', maxCount: null },
  { type: 'bestseller_products', maxCount: null },
  { type: 'deals_products', maxCount: null },
  { type: 'image_text', maxCount: null },
  { type: 'faq', maxCount: null },
  { type: 'newsletter', maxCount: null },
];

const SECTION_TYPE_DEFAULT_SETTINGS = {
  hero: { height: 'normal' },
  categories: {},
  featured_products: { title: 'Uitgelicht' },
  new_products: { title: 'Nieuw binnen' },
  bestseller_products: { title: 'Meest verkocht' },
  deals_products: { title: 'Acties' },
  image_text: { title: '', body: '', image_position: 'right', image: null, primary_cta: null },
  faq: { title: 'Veelgestelde vragen', items: [] },
  newsletter: { title: 'Blijf op de hoogte', subtitle: 'Meld je aan voor onze nieuwsbrief.', button_text: 'Aanmelden' },
};

const TEXT_ALIGN_TO_LAYOUT = {
  links: 'background_image_text_left',
  centrum: 'background_image_centered_text',
  rechts: 'background_image_text_right',
};
const LAYOUT_TO_TEXT_ALIGN = Object.fromEntries(
  Object.entries(TEXT_ALIGN_TO_LAYOUT).map(([align, layout]) => [layout, align])
);

const HEIGHT_OPTIONS = [
  { value: 'compact', label: 'Compact' },
  { value: 'normal', label: 'Normaal' },
  { value: 'large', label: 'Groot' },
];

const homeSections = (theme) => theme?.draft?.pages?.home?.sections || [];

const updateSectionsInDraft = (theme, sectionId, updater) => {
  const sections = homeSections(theme).map((section) => (
    section.id === sectionId ? updater(section) : section
  ));
  return {
    ...theme,
    draft: {
      ...theme.draft,
      pages: { ...theme.draft.pages, home: { ...theme.draft.pages.home, sections } },
    },
  };
};

const IMAGE_SOURCE_TABS = [
  { id: 'upload', label: 'Uploaden' },
  { id: 'library', label: 'Bibliotheek' },
  { id: 'url', label: 'Externe URL' },
];

const ImagePicker = ({ image, onChange }) => {
  const [tab, setTab] = useState(image?.source_type === 'media' ? 'library' : (image?.external_url ? 'url' : 'upload'));
  const [uploading, setUploading] = useState(false);
  const [library, setLibrary] = useState(null); // null = not fetched yet
  const [libraryLoading, setLibraryLoading] = useState(false);
  const fileInputRef = useRef(null);
  const currentUrl = resolveCommerceImageUrl(image);

  const loadLibrary = async () => {
    try {
      setLibraryLoading(true);
      const res = await axios.get(`${API}/commerce/media`);
      setLibrary(res.data);
    } catch (error) {
      toast.error('Kon mediabibliotheek niet laden');
      setLibrary([]);
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'library' && library === null) loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, library]);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/commerce/media`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange({ source_type: 'media', media_id: res.data.id, external_url: null, alt_text: image?.alt_text || null, focal_point: null });
      setLibrary(null);
      toast.success('Afbeelding geüpload');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Uploaden mislukt');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <Label>Achtergrondafbeelding</Label>
      {currentUrl ? (
        <div className="relative rounded-xl overflow-hidden border border-slate-200">
          <img src={currentUrl} alt={image?.alt_text || ''} className="w-full h-32 object-cover" />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow"
            title="Afbeelding verwijderen"
          >
            <Trash2 className="w-4 h-4 text-slate-700" />
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-slate-300 h-32 flex items-center justify-center text-slate-400">
          <ImageOff className="w-6 h-6" />
        </div>
      )}

      <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
        {IMAGE_SOURCE_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex-1 text-xs font-medium rounded-md py-1.5 transition-colors ${tab === t.id ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'upload' && (
        <div>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileSelected} className="hidden" />
          <Button type="button" variant="outline" className="rounded-xl w-full" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploaden...' : 'Afbeelding kiezen (JPG, PNG, WEBP, max 8MB)'}
          </Button>
        </div>
      )}

      {tab === 'library' && (
        libraryLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : library && library.length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            {library.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onChange({ source_type: 'media', media_id: item.id, external_url: null, alt_text: image?.alt_text || item.alt_text || null, focal_point: null })}
                className={`aspect-square rounded-lg overflow-hidden border-2 ${image?.source_type === 'media' && image?.media_id === item.id ? 'border-teal-500' : 'border-transparent hover:border-slate-300'}`}
              >
                <img src={resolveCommerceImageUrl({ source_type: 'media', media_id: item.id })} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-6">Nog geen afbeeldingen geüpload.</p>
        )
      )}

      {tab === 'url' && (
        <Input
          value={image?.source_type === 'external_url' ? (image.external_url || '') : ''}
          onChange={(e) => {
            const url = e.target.value;
            onChange(url ? { source_type: 'external_url', media_id: null, external_url: url, alt_text: image?.alt_text || null, focal_point: null } : null);
          }}
          placeholder="https://..."
        />
      )}

      <div>
        <Label htmlFor="hero-image-alt">Alt-tekst (voor screenreaders)</Label>
        <Input
          id="hero-image-alt"
          value={image?.alt_text || ''}
          onChange={(e) => onChange(image ? { ...image, alt_text: e.target.value } : null)}
          placeholder="Beschrijving van de afbeelding"
        />
      </div>
    </div>
  );
};

const HeroSettingsForm = ({ section, organizationName, onPatchSection, onPatchSettings }) => {
  const settings = section.settings || {};
  const overlay = settings.overlay || {};
  const image = settings.image || null;
  const hasImage = section.layout_variant !== 'minimal_no_image';
  const textAlign = LAYOUT_TO_TEXT_ALIGN[section.layout_variant] || 'centrum';
  const defaultTitle = `Welkom in de webshop van ${organizationName || 'jouw organisatie'}`;
  const defaultSubtitle = 'Boeken, merchandise en meer — rechtstreeks van onze gemeente.';

  const handleImageChange = (newImage) => {
    onPatchSettings({
      image: newImage,
      overlay: { ...overlay, enabled: Boolean(newImage) },
    });
    onPatchSection({ layout_variant: newImage ? TEXT_ALIGN_TO_LAYOUT[textAlign] : 'minimal_no_image' });
  };

  const badge = settings.badge || {};
  const badgeEnabled = badge.enabled !== false;
  const defaultBadgeText = `${(organizationName || 'jouw organisatie')} · shop`.toUpperCase();

  return (
    <div className="space-y-4">
      <ImagePicker image={image} onChange={handleImageChange} />
      <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5">
        <div>
          <Label htmlFor="hero-badge-toggle" className="cursor-pointer">Badge tonen</Label>
          <p className="text-xs text-slate-400">Klein label boven de titel, bijv. "{defaultBadgeText}"</p>
        </div>
        <Switch
          id="hero-badge-toggle"
          checked={badgeEnabled}
          onCheckedChange={(checked) => onPatchSettings({ badge: { ...badge, enabled: checked } })}
        />
      </div>
      {badgeEnabled && (
        <div>
          <Label htmlFor="hero-badge-text">Badge-tekst</Label>
          <Input
            id="hero-badge-text"
            value={badge.text || ''}
            onChange={(e) => onPatchSettings({ badge: { ...badge, text: e.target.value } })}
            placeholder={defaultBadgeText}
          />
        </div>
      )}
      <div>
        <Label htmlFor="hero-title">Titel</Label>
        <Input
          id="hero-title"
          value={settings.title || ''}
          onChange={(e) => onPatchSettings({ title: e.target.value })}
          placeholder={hasImage ? 'Leeg laten toont geen titel' : defaultTitle}
        />
        {!settings.title && (
          <p className="text-xs text-slate-400 mt-1">
            {hasImage
              ? 'Leeg = geen titel getoond — handig als je afbeelding zelf al tekst bevat.'
              : `Leeg = toont standaard "${defaultTitle}" op de winkelpagina.`}
          </p>
        )}
      </div>
      <div>
        <Label htmlFor="hero-subtitle">Subtitel</Label>
        <Input
          id="hero-subtitle"
          value={settings.subtitle || ''}
          onChange={(e) => onPatchSettings({ subtitle: e.target.value })}
          placeholder={hasImage ? 'Leeg laten toont geen subtitel' : defaultSubtitle}
        />
        {!settings.subtitle && (
          <p className="text-xs text-slate-400 mt-1">
            {hasImage ? 'Leeg = geen subtitel getoond.' : `Leeg = toont standaard "${defaultSubtitle}"`}
          </p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="hero-cta-text">Knoptekst</Label>
          <Input
            id="hero-cta-text"
            value={settings.primary_cta?.text || ''}
            onChange={(e) => onPatchSettings({ primary_cta: { ...settings.primary_cta, text: e.target.value } })}
            placeholder="Bekijk producten"
          />
        </div>
        <div>
          <Label htmlFor="hero-cta-link">Knoplink</Label>
          <Input
            id="hero-cta-link"
            value={settings.primary_cta?.link || ''}
            onChange={(e) => onPatchSettings({ primary_cta: { ...settings.primary_cta, link: e.target.value } })}
            placeholder="/shop"
          />
        </div>
      </div>
      {hasImage && (
        <div>
          <Label>Tekstuitlijning</Label>
          <Select value={textAlign} onValueChange={(value) => onPatchSection({ layout_variant: TEXT_ALIGN_TO_LAYOUT[value] })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="links">Links</SelectItem>
              <SelectItem value="centrum">Centrum</SelectItem>
              <SelectItem value="rechts">Rechts</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label>Bannerhoogte</Label>
        <Select value={settings.height || 'normal'} onValueChange={(value) => onPatchSettings({ height: value })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {HEIGHT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {hasImage && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="hero-overlay-color">Overlaykleur</Label>
            <div className="flex items-center gap-2">
              <input
                id="hero-overlay-color"
                type="color"
                value={overlay.color || '#0F766E'}
                onChange={(e) => onPatchSettings({ overlay: { ...overlay, color: e.target.value } })}
                className="h-9 w-12 rounded border border-slate-200"
              />
              <Input value={overlay.color || '#0F766E'} onChange={(e) => onPatchSettings({ overlay: { ...overlay, color: e.target.value } })} />
            </div>
          </div>
          <div>
            <Label htmlFor="hero-overlay-strength">Overlaysterkte (%)</Label>
            <Input
              id="hero-overlay-strength"
              type="number"
              min="0"
              max="100"
              value={overlay.strength ?? 60}
              onChange={(e) => onPatchSettings({ overlay: { ...overlay, strength: Number(e.target.value) } })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const GenericSectionSettingsForm = ({ section, onPatchSettings }) => (
  <div className="space-y-4">
    <div>
      <Label htmlFor="section-title">Titel</Label>
      <Input
        id="section-title"
        value={section.settings?.title || ''}
        onChange={(e) => onPatchSettings({ title: e.target.value })}
      />
    </div>
    <p className="text-sm text-slate-500">
      De producten in deze sectie volgen automatisch uit je assortiment — dat is nog niet hier aan te passen.
    </p>
  </div>
);

const ImageTextSettingsForm = ({ section, onPatchSettings }) => {
  const settings = section.settings || {};
  return (
    <div className="space-y-4">
      <ImagePicker image={settings.image || null} onChange={(image) => onPatchSettings({ image })} />
      <div>
        <Label htmlFor="imgtext-title">Titel</Label>
        <Input id="imgtext-title" value={settings.title || ''} onChange={(e) => onPatchSettings({ title: e.target.value })} />
      </div>
      <div>
        <Label htmlFor="imgtext-body">Tekst</Label>
        <Textarea id="imgtext-body" rows={4} value={settings.body || ''} onChange={(e) => onPatchSettings({ body: e.target.value })} />
      </div>
      <div>
        <Label>Afbeeldingspositie</Label>
        <Select value={settings.image_position || 'right'} onValueChange={(value) => onPatchSettings({ image_position: value })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Links</SelectItem>
            <SelectItem value="right">Rechts</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="imgtext-cta-text">Knoptekst</Label>
          <Input
            id="imgtext-cta-text"
            value={settings.primary_cta?.text || ''}
            onChange={(e) => onPatchSettings({ primary_cta: { ...settings.primary_cta, text: e.target.value } })}
          />
        </div>
        <div>
          <Label htmlFor="imgtext-cta-link">Knoplink</Label>
          <Input
            id="imgtext-cta-link"
            value={settings.primary_cta?.link || ''}
            onChange={(e) => onPatchSettings({ primary_cta: { ...settings.primary_cta, link: e.target.value } })}
          />
        </div>
      </div>
    </div>
  );
};

const FaqSettingsForm = ({ section, onPatchSettings }) => {
  const settings = section.settings || {};
  const items = settings.items || [];

  const updateItem = (index, patch) => {
    onPatchSettings({ items: items.map((item, i) => (i === index ? { ...item, ...patch } : item)) });
  };
  const addItem = () => onPatchSettings({ items: [...items, { question: '', answer: '' }] });
  const removeItem = (index) => onPatchSettings({ items: items.filter((_, i) => i !== index) });

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="faq-title">Titel</Label>
        <Input id="faq-title" value={settings.title || ''} onChange={(e) => onPatchSettings({ title: e.target.value })} />
      </div>
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="rounded-xl border border-slate-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-400">Vraag {index + 1}</Label>
              <button type="button" onClick={() => removeItem(index)} className="text-slate-300 hover:text-red-500" aria-label="Vraag verwijderen">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <Input value={item.question} onChange={(e) => updateItem(index, { question: e.target.value })} placeholder="Vraag" />
            <Textarea rows={2} value={item.answer} onChange={(e) => updateItem(index, { answer: e.target.value })} placeholder="Antwoord" />
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" className="rounded-xl w-full" onClick={addItem}>
        <Plus className="w-4 h-4 mr-2" />
        Vraag toevoegen
      </Button>
    </div>
  );
};

const NewsletterSettingsForm = ({ section, onPatchSettings }) => {
  const settings = section.settings || {};
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="newsletter-title">Titel</Label>
        <Input id="newsletter-title" value={settings.title || ''} onChange={(e) => onPatchSettings({ title: e.target.value })} />
      </div>
      <div>
        <Label htmlFor="newsletter-subtitle">Subtitel</Label>
        <Input id="newsletter-subtitle" value={settings.subtitle || ''} onChange={(e) => onPatchSettings({ subtitle: e.target.value })} />
      </div>
      <div>
        <Label htmlFor="newsletter-button">Knoptekst</Label>
        <Input id="newsletter-button" value={settings.button_text || ''} onChange={(e) => onPatchSettings({ button_text: e.target.value })} placeholder="Aanmelden" />
      </div>
      <p className="text-sm text-slate-500">Aanmeldingen komen binnen via de nieuwsbrief-API van je organisatie.</p>
    </div>
  );
};

const SectionSettingsForm = ({ section, organizationName, onPatchSection, onPatchSettings }) => {
  if (section.type === 'hero') {
    return <HeroSettingsForm section={section} organizationName={organizationName} onPatchSection={onPatchSection} onPatchSettings={onPatchSettings} />;
  }
  if (section.type === 'image_text') {
    return <ImageTextSettingsForm section={section} onPatchSettings={onPatchSettings} />;
  }
  if (section.type === 'faq') {
    return <FaqSettingsForm section={section} onPatchSettings={onPatchSettings} />;
  }
  if (section.type === 'newsletter') {
    return <NewsletterSettingsForm section={section} onPatchSettings={onPatchSettings} />;
  }
  return <GenericSectionSettingsForm section={section} onPatchSettings={onPatchSettings} />;
};

const DEFAULT_COLORS = {
  primary: '#0F766E', accent: '#F59E0B', page_background: '#F8FAFC', secondary_background: '#FFFFFF',
  text_primary: '#0F172A', text_secondary: '#475569', border: '#E2E8F0', success: '#16A34A', warning: '#D97706', error: '#DC2626',
};

const FONT_OPTIONS = [
  { value: 'manrope', label: 'Manrope' },
  { value: 'inter', label: 'Inter' },
  { value: 'fraunces', label: 'Fraunces' },
];
const SCALE_OPTIONS = [
  { value: 'compact', label: 'Compact' },
  { value: 'normal', label: 'Normaal' },
  { value: 'large', label: 'Groot' },
];
const SHAPE_OPTIONS = [
  { value: 'square', label: 'Vierkant' },
  { value: 'rounded', label: 'Afgerond' },
  { value: 'pill', label: 'Pil' },
];
const RADIUS_OPTIONS = [
  { value: 'sm', label: 'Klein' },
  { value: 'md', label: 'Middel' },
  { value: 'lg', label: 'Groot' },
  { value: 'xl', label: 'Extra groot' },
];
const SHADOW_OPTIONS = [
  { value: 'none', label: 'Geen' },
  { value: 'sm', label: 'Subtiel' },
  { value: 'md', label: 'Normaal' },
];

const PRESETS = [
  {
    id: 'modern', label: 'Modern',
    colors: { primary: '#0F172A', accent: '#6366F1', page_background: '#F8FAFC', secondary_background: '#FFFFFF', text_primary: '#0F172A', text_secondary: '#64748B', border: '#E2E8F0', success: '#16A34A', warning: '#D97706', error: '#DC2626' },
    typography: { heading_font: 'inter', body_font: 'inter', scale: 'normal' },
    buttons: { shape: 'square' },
    cards: { corner_radius: 'md', shadow_strength: 'sm' },
  },
  {
    id: 'warm', label: 'Warm',
    colors: { primary: '#C2410C', accent: '#F59E0B', page_background: '#FFF7ED', secondary_background: '#FFFFFF', text_primary: '#431407', text_secondary: '#9A3412', border: '#FED7AA', success: '#16A34A', warning: '#D97706', error: '#DC2626' },
    typography: { heading_font: 'fraunces', body_font: 'inter', scale: 'normal' },
    buttons: { shape: 'rounded' },
    cards: { corner_radius: 'lg', shadow_strength: 'sm' },
  },
  {
    id: 'community', label: 'Community',
    colors: { primary: '#0F766E', accent: '#F59E0B', page_background: '#F8FAFC', secondary_background: '#FFFFFF', text_primary: '#0F172A', text_secondary: '#475569', border: '#E2E8F0', success: '#16A34A', warning: '#D97706', error: '#DC2626' },
    typography: { heading_font: 'manrope', body_font: 'inter', scale: 'normal' },
    buttons: { shape: 'rounded' },
    cards: { corner_radius: 'lg', shadow_strength: 'sm' },
  },
  {
    id: 'merchandise', label: 'Merchandise',
    colors: { primary: '#111827', accent: '#DC2626', page_background: '#FFFFFF', secondary_background: '#F3F4F6', text_primary: '#111827', text_secondary: '#6B7280', border: '#E5E7EB', success: '#16A34A', warning: '#D97706', error: '#DC2626' },
    typography: { heading_font: 'manrope', body_font: 'inter', scale: 'large' },
    buttons: { shape: 'pill' },
    cards: { corner_radius: 'xl', shadow_strength: 'md' },
  },
  {
    id: 'minimal', label: 'Minimal',
    colors: { primary: '#18181B', accent: '#71717A', page_background: '#FFFFFF', secondary_background: '#FAFAFA', text_primary: '#18181B', text_secondary: '#71717A', border: '#E4E4E7', success: '#16A34A', warning: '#D97706', error: '#DC2626' },
    typography: { heading_font: 'inter', body_font: 'inter', scale: 'compact' },
    buttons: { shape: 'square' },
    cards: { corner_radius: 'sm', shadow_strength: 'none' },
  },
];

// WCAG 2.1 relative-luminance contrast ratio (opdracht §19 "kleurcontrastcontrole").
// Pure/no dependency so it's cheap to run on every keystroke.
const hexToRgb = (hex) => {
  const clean = (hex || '').replace('#', '').trim();
  const normalized = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
  const bigint = parseInt(normalized, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
};

const relativeLuminance = ({ r, g, b }) => {
  const [rs, gs, bs] = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const contrastRatio = (hex1, hex2) => {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return null;
  const l1 = relativeLuminance(rgb1);
  const l2 = relativeLuminance(rgb2);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

const WCAG_AA_NORMAL_TEXT_RATIO = 4.5;

const ContrastWarning = ({ ratio, label }) => {
  if (ratio === null || ratio >= WCAG_AA_NORMAL_TEXT_RATIO) return null;
  return (
    <p className="text-xs text-amber-600 mt-1">
      ⚠ {label}: contrast {ratio.toFixed(1)}:1 — WCAG AA vraagt minimaal 4.5:1 voor leesbare tekst.
    </p>
  );
};

const ColorField = ({ label, value, onChange }) => (
  <div>
    <Label>{label}</Label>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 rounded border border-slate-200 shrink-0" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  </div>
);

const ColorsForm = ({ colors, onPatch }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const c = { ...DEFAULT_COLORS, ...colors };
  const textOnPageRatio = contrastRatio(c.text_primary, c.page_background);
  const primaryOnWhiteRatio = contrastRatio(c.primary, '#FFFFFF');
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <ColorField label="Primaire kleur" value={c.primary} onChange={(v) => onPatch({ primary: v })} />
        <ColorField label="Accentkleur" value={c.accent} onChange={(v) => onPatch({ accent: v })} />
        <ColorField label="Paginaachtergrond" value={c.page_background} onChange={(v) => onPatch({ page_background: v })} />
        <ColorField label="Primaire tekstkleur" value={c.text_primary} onChange={(v) => onPatch({ text_primary: v })} />
      </div>
      <ContrastWarning ratio={textOnPageRatio} label="Primaire tekstkleur op paginaachtergrond" />
      <ContrastWarning ratio={primaryOnWhiteRatio} label="Primaire kleur op een witte knop (Hero-knoptekst)" />
      <button type="button" className="text-sm text-teal-700 font-medium" onClick={() => setShowAdvanced((v) => !v)}>
        {showAdvanced ? 'Verberg geavanceerde kleuren' : 'Geavanceerde kleuren tonen'}
      </button>
      {showAdvanced && (
        <div className="grid grid-cols-2 gap-4">
          <ColorField label="Secundaire achtergrond" value={c.secondary_background} onChange={(v) => onPatch({ secondary_background: v })} />
          <ColorField label="Secundaire tekstkleur" value={c.text_secondary} onChange={(v) => onPatch({ text_secondary: v })} />
          <ColorField label="Randkleur" value={c.border} onChange={(v) => onPatch({ border: v })} />
          <ColorField label="Succeskleur" value={c.success} onChange={(v) => onPatch({ success: v })} />
          <ColorField label="Waarschuwingskleur" value={c.warning} onChange={(v) => onPatch({ warning: v })} />
          <ColorField label="Foutkleur" value={c.error} onChange={(v) => onPatch({ error: v })} />
        </div>
      )}
    </div>
  );
};

const TypographyForm = ({ typography, onPatch }) => {
  const t = { heading_font: 'manrope', body_font: 'inter', scale: 'normal', ...typography };
  return (
    <div className="space-y-5">
      <div>
        <Label>Lettertype koppen</Label>
        <Select value={t.heading_font} onValueChange={(v) => onPatch({ heading_font: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{FONT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Lettertype tekst</Label>
        <Select value={t.body_font} onValueChange={(v) => onPatch({ body_font: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{FONT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Lettergrootteschaal</Label>
        <Select value={t.scale} onValueChange={(v) => onPatch({ scale: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{SCALE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
};

const ButtonsCardsForm = ({ buttons, cards, onPatchButtons, onPatchCards }) => {
  const b = { shape: 'rounded', ...buttons };
  const c = { corner_radius: 'lg', shadow_strength: 'sm', ...cards };
  return (
    <div className="space-y-5">
      <div>
        <Label>Knopvorm</Label>
        <Select value={b.shape} onValueChange={(v) => onPatchButtons({ shape: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{SHAPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Hoekafronding kaarten</Label>
        <Select value={c.corner_radius} onValueChange={(v) => onPatchCards({ corner_radius: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{RADIUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Schaduwsterkte kaarten</Label>
        <Select value={c.shadow_strength} onValueChange={(v) => onPatchCards({ shadow_strength: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{SHADOW_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
};

const PresetsPanel = ({ onApply }) => (
  <div className="space-y-3">
    <p className="text-sm text-slate-500">
      Past kleuren, lettertype, knopvorm en kaartstijl toe. Producten, teksten en afbeeldingen blijven ongewijzigd, en niets wordt gepubliceerd totdat je dat zelf doet.
    </p>
    <div className="grid grid-cols-2 gap-3">
      {PRESETS.map((preset) => (
        <button
          key={preset.id}
          type="button"
          onClick={() => onApply(preset)}
          className="rounded-xl border border-slate-200 p-4 text-left hover:border-teal-400 hover:bg-teal-50/50 transition-colors"
        >
          <div className="flex gap-1.5 mb-2">
            <span className="w-5 h-5 rounded-full border border-black/5" style={{ backgroundColor: preset.colors.primary }} />
            <span className="w-5 h-5 rounded-full border border-black/5" style={{ backgroundColor: preset.colors.accent }} />
          </div>
          <p className="text-sm font-medium text-slate-900">{preset.label}</p>
        </button>
      ))}
    </div>
  </div>
);

const MAX_FOOTER_COLUMNS = 3;
const MAX_FOOTER_LINKS_PER_COLUMN = 6;

const FooterSettingsForm = ({ footer, onPatch }) => {
  const tagline = footer?.tagline || '';
  const contact = footer?.contact || {};
  const columns = footer?.link_columns || [];

  const updateColumn = (index, patch) => {
    onPatch({ link_columns: columns.map((col, i) => (i === index ? { ...col, ...patch } : col)) });
  };
  const addColumn = () => {
    if (columns.length >= MAX_FOOTER_COLUMNS) return;
    onPatch({ link_columns: [...columns, { title: '', links: [] }] });
  };
  const removeColumn = (index) => onPatch({ link_columns: columns.filter((_, i) => i !== index) });

  const updateLink = (colIndex, linkIndex, patch) => {
    const links = (columns[colIndex].links || []).map((link, i) => (i === linkIndex ? { ...link, ...patch } : link));
    updateColumn(colIndex, { links });
  };
  const addLink = (colIndex) => {
    const links = columns[colIndex].links || [];
    if (links.length >= MAX_FOOTER_LINKS_PER_COLUMN) return;
    updateColumn(colIndex, { links: [...links, { label: '', url: '' }] });
  };
  const removeLink = (colIndex, linkIndex) => {
    updateColumn(colIndex, { links: (columns[colIndex].links || []).filter((_, i) => i !== linkIndex) });
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <Label htmlFor="footer-tagline">Tagline</Label>
        <Textarea
          id="footer-tagline"
          rows={2}
          value={tagline}
          onChange={(e) => onPatch({ tagline: e.target.value })}
          placeholder="Korte omschrijving onder je naam, bijv. 'Boeken, merchandise en meer.'"
        />
      </div>

      <div className="space-y-3 pt-6 border-t border-slate-100">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Contactgegevens</p>
        <div className="space-y-2">
          <Label htmlFor="footer-email">E-mailadres</Label>
          <Input id="footer-email" type="email" value={contact.email || ''} onChange={(e) => onPatch({ contact: { ...contact, email: e.target.value } })} placeholder="info@voorbeeld.nl" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="footer-phone">Telefoonnummer</Label>
          <Input id="footer-phone" value={contact.phone || ''} onChange={(e) => onPatch({ contact: { ...contact, phone: e.target.value } })} placeholder="06 12345678" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="footer-address">Adres</Label>
          <Input id="footer-address" value={contact.address || ''} onChange={(e) => onPatch({ contact: { ...contact, address: e.target.value } })} placeholder="Kerkstraat 1, Winterswijk" />
        </div>
      </div>

      <div className="space-y-4 pt-6 border-t border-slate-100">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Linkkolommen</p>
          <Button type="button" variant="outline" size="sm" onClick={addColumn} disabled={columns.length >= MAX_FOOTER_COLUMNS}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Kolom
          </Button>
        </div>
        {columns.length === 0 && (
          <p className="text-sm text-slate-400">Nog geen kolommen — voeg er een toe voor bijv. "Klantenservice" of "Over ons".</p>
        )}
        {columns.map((column, colIndex) => (
          <div key={colIndex} className="rounded-xl border border-slate-200 p-3 space-y-3">
            <div className="flex items-center gap-2">
              <Input
                value={column.title || ''}
                onChange={(e) => updateColumn(colIndex, { title: e.target.value })}
                placeholder="Kolomtitel, bijv. Klantenservice"
                className="flex-1"
              />
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removeColumn(colIndex)}>
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </div>
            {(column.links || []).map((link, linkIndex) => (
              <div key={linkIndex} className="flex items-center gap-2 pl-3">
                <Input
                  value={link.label || ''}
                  onChange={(e) => updateLink(colIndex, linkIndex, { label: e.target.value })}
                  placeholder="Tekst"
                  className="flex-1"
                />
                <Input
                  value={link.url || ''}
                  onChange={(e) => updateLink(colIndex, linkIndex, { url: e.target.value })}
                  placeholder="/pagina of https://..."
                  className="flex-1"
                />
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removeLink(colIndex, linkIndex)}>
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => addLink(colIndex)}
              disabled={(column.links || []).length >= MAX_FOOTER_LINKS_PER_COLUMN}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Link toevoegen
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const StyleSettingsForms = ({ settings, onPatchGroup, onApplyPreset }) => (
  <div className="space-y-10">
    <div>
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Kleuren</p>
      <ColorsForm colors={settings?.colors} onPatch={(patch) => onPatchGroup('colors', patch)} />
    </div>
    <div className="pt-8 border-t border-slate-100">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Typografie</p>
      <TypographyForm typography={settings?.typography} onPatch={(patch) => onPatchGroup('typography', patch)} />
    </div>
    <div className="pt-8 border-t border-slate-100">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Knoppen &amp; kaarten</p>
      <ButtonsCardsForm
        buttons={settings?.buttons}
        cards={settings?.cards}
        onPatchButtons={(patch) => onPatchGroup('buttons', patch)}
        onPatchCards={(patch) => onPatchGroup('cards', patch)}
      />
    </div>
    <div className="pt-8 border-t border-slate-100">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Footer</p>
      <FooterSettingsForm footer={settings?.footer} onPatch={(patch) => onPatchGroup('footer', patch)} />
    </div>
    <div className="pt-8 border-t border-slate-100">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Presets</p>
      <PresetsPanel onApply={onApplyPreset} />
    </div>
  </div>
);

const SectionPreviewPlaceholder = ({ section }) => (
  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 space-y-3">
    <h2 className="text-lg font-semibold text-slate-900">{section.settings?.title || SECTION_LABELS[section.type]}</h2>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="aspect-square rounded-xl bg-slate-200" />
      ))}
    </div>
  </div>
);

const SectionLoadingGrid = ({ title }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="aspect-square rounded-xl bg-slate-100 animate-pulse" />
      ))}
    </div>
  </div>
);

// Explicit "nothing here yet" state instead of a generic mockup -- makes
// clear the section works but has no data, and offers a way to fix that.
const SectionEmptyState = ({ title, message }) => {
  const navigate = useNavigate();
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 space-y-3 text-center">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500">{message}</p>
      <Button type="button" variant="outline" size="sm" onClick={() => navigate('/commerce')}>
        Producten beheren
      </Button>
    </div>
  );
};

// Read-only card -- deliberately not the storefront's ProductCard, which
// needs ShopCartProvider/ShopFavoritesProvider that this admin page doesn't
// mount (and cart/favorite actions make no sense inside an editor preview).
const PreviewProductGrid = ({ section, products, loading }) => {
  const title = section.settings?.title || SECTION_LABELS[section.type];
  if (loading) return <SectionLoadingGrid title={title} />;
  if (products.length === 0) {
    return <SectionEmptyState title={title} message={PREVIEW_EMPTY_COPY[section.type]} />;
  }
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {products.slice(0, 4).map((product) => (
          <div key={product.id} className="rounded-xl border border-slate-100 bg-white overflow-hidden">
            <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
              {product.image_url ? (
                <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
              ) : (
                <Package className="w-8 h-8 text-slate-300" />
              )}
            </div>
            <div className="p-2 space-y-1">
              <p className="text-xs font-medium text-slate-700 line-clamp-1">{product.title}</p>
              <PriceTag price={product.price} salePrice={product.sale_price} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const PreviewCategoriesGrid = ({ section, categories, loading }) => {
  const title = section.settings?.title || SECTION_LABELS.categories;
  if (loading) return <SectionLoadingGrid title={title} />;
  if (categories.length === 0) {
    return <SectionEmptyState title={title} message={PREVIEW_EMPTY_COPY.categories} />;
  }
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {categories.slice(0, 4).map((category) => (
          <div key={category.id} className="flex flex-col items-center gap-2 bg-white rounded-xl border border-slate-100 p-4">
            <span className="text-2xl">{getCategoryIcon(category.name)}</span>
            <span className="text-xs text-slate-700 text-center line-clamp-1">{category.name}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const LivePreview = ({ organizationName, sections, settings, categories, productCollections, previewDataLoading }) => (
  <div
    className="max-w-3xl mx-auto space-y-6 py-2 rounded-2xl"
    style={settings?.colors?.page_background ? { backgroundColor: settings.colors.page_background } : undefined}
  >
    {sections.filter((s) => s.enabled).map((section) => (
      <div key={section.id}>
        {section.type === 'hero' ? (
          <Hero organizationName={organizationName} section={section} theme={settings} onCtaClick={() => {}} />
        ) : section.type === 'image_text' ? (
          <ImageTextSection section={section} theme={settings} />
        ) : section.type === 'faq' ? (
          (section.settings?.items || []).length > 0 ? <FaqSection section={section} /> : <SectionPreviewPlaceholder section={section} />
        ) : section.type === 'newsletter' ? (
          <NewsletterSection section={section} theme={settings} />
        ) : section.type === 'categories' ? (
          <PreviewCategoriesGrid section={section} categories={categories} loading={previewDataLoading} />
        ) : PRODUCT_ROW_TYPES.includes(section.type) ? (
          <PreviewProductGrid section={section} products={productCollections[section.type] || []} loading={previewDataLoading} />
        ) : (
          <SectionPreviewPlaceholder section={section} />
        )}
      </div>
    ))}
    {sections.every((s) => !s.enabled) && (
      <p className="text-center text-sm text-slate-400 py-12">Alle secties staan uit — bezoekers zien een lege homepage.</p>
    )}
    <div className="rounded-2xl overflow-hidden border border-slate-100">
      <ShopFooter organizationName={organizationName} footer={settings?.footer} />
    </div>
  </div>
);

const SortableSectionRow = ({ section, selectedId, onSelect, onToggleEnabled, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`w-full flex items-center gap-0.5 rounded-xl pl-1 pr-2 py-1 transition-colors ${
        selectedId === section.id ? 'bg-teal-50 text-teal-900' : 'hover:bg-slate-50 text-slate-700'
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        title="Sleep om te herordenen"
        className="p-1.5 shrink-0 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200 cursor-grab active:cursor-grabbing touch-none"
        aria-label={`Versleep ${SECTION_LABELS[section.type] || section.type} om de volgorde te wijzigen`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => onSelect(section.id)}
        className="flex-1 min-w-0 text-left py-1.5 text-sm font-medium truncate"
      >
        {SECTION_LABELS[section.type] || section.type}
      </button>
      <div className="flex flex-col shrink-0">
        <button
          type="button"
          disabled={isFirst}
          onClick={() => onMoveUp(section.id)}
          className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-30 disabled:hover:text-slate-300"
          aria-label={`${SECTION_LABELS[section.type] || section.type} omhoog verplaatsen`}
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          disabled={isLast}
          onClick={() => onMoveDown(section.id)}
          className="p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-30 disabled:hover:text-slate-300"
          aria-label={`${SECTION_LABELS[section.type] || section.type} omlaag verplaatsen`}
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
      <Switch checked={section.enabled} onCheckedChange={() => onToggleEnabled(section.id)} />
    </div>
  );
};

// Drag-and-drop (pointer + keyboard, see @dnd-kit) with a visible handle so
// dragging never starts from an accidental tap/scroll -- Omhoog/Omlaag
// buttons cover the same reordering without a pointer, for keyboard-averse
// or touch-imprecise use (opdracht §11.1-§11.3).
const SectionList = ({ sections, selectedId, onSelect, onToggleEnabled, onReorder }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex((s) => s.id === active.id);
    const newIndex = sections.findIndex((s) => s.id === over.id);
    onReorder(arrayMove(sections, oldIndex, newIndex));
  };

  const moveBy = (id, delta) => {
    const index = sections.findIndex((s) => s.id === id);
    const newIndex = index + delta;
    if (newIndex < 0 || newIndex >= sections.length) return;
    onReorder(arrayMove(sections, index, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {sections.map((section, index) => (
            <SortableSectionRow
              key={section.id}
              section={section}
              selectedId={selectedId}
              onSelect={onSelect}
              onToggleEnabled={onToggleEnabled}
              onMoveUp={(id) => moveBy(id, -1)}
              onMoveDown={(id) => moveBy(id, 1)}
              isFirst={index === 0}
              isLast={index === sections.length - 1}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

const AddSectionMenu = ({ sections, onAdd }) => {
  const counts = sections.reduce((acc, s) => ({ ...acc, [s.type]: (acc[s.type] || 0) + 1 }), {});
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="rounded-xl w-full mt-3">
          <Plus className="w-4 h-4 mr-2" />
          Sectie toevoegen
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {ADDABLE_SECTION_TYPES.map(({ type, maxCount }) => {
          const reachedMax = maxCount !== null && (counts[type] || 0) >= maxCount;
          return (
            <DropdownMenuItem key={type} disabled={reachedMax} onClick={() => onAdd(type)}>
              <span className="flex-1">{SECTION_LABELS[type] || type}</span>
              {reachedMax && <span className="text-xs text-slate-400">max.</span>}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ThemeEditorPage = () => {
  const { user, hasCapability } = useAuth();
  const navigate = useNavigate();
  const canEdit = hasCapability('commerce_theme_edit');
  const canPublish = hasCapability('commerce_theme_publish');
  const orgId = user?.organization_id;

  const [theme, setTheme] = useState(null);
  const [organizationName, setOrganizationName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [mode, setMode] = useState('sections');
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [restoringRevisionId, setRestoringRevisionId] = useState(null);
  const [sharingPreview, setSharingPreview] = useState(false);
  const [previewCategories, setPreviewCategories] = useState([]);
  const [previewProducts, setPreviewProducts] = useState([]);
  const [previewBestsellerIds, setPreviewBestsellerIds] = useState([]);
  const [previewDataLoading, setPreviewDataLoading] = useState(true);

  useEffect(() => {
    if (!canEdit || !orgId) return;
    (async () => {
      try {
        setLoading(true);
        const [themeRes, brandingRes] = await Promise.all([
          axios.get(`${API}/commerce/shops/${orgId}/theme/draft`),
          axios.get(`${API}/organization/branding`),
        ]);
        setTheme(themeRes.data);
        setOrganizationName(brandingRes.data?.name || '');
        setOrgSlug(brandingRes.data?.slug || '');
        const sections = homeSections(themeRes.data);
        if (sections.length > 0) setSelectedSectionId(sections[0].id);
        setDirty(false);
      } catch (error) {
        toast.error('Kon vormgeving niet laden');
      } finally {
        setLoading(false);
      }
    })();
  }, [canEdit, orgId]);

  // Same public endpoints the live storefront uses, so the "Categorieën"/
  // "Uitgelicht"/etc. section previews show exactly what a visitor would
  // see instead of a generic mockup grid.
  useEffect(() => {
    if (!canEdit || !orgId) return;
    (async () => {
      try {
        const [categoriesRes, productsRes, bestsellersRes] = await Promise.all([
          axios.get(`${API}/public/commerce/${orgId}/categories`),
          axios.get(`${API}/public/commerce/${orgId}/products`),
          axios.get(`${API}/public/commerce/${orgId}/bestsellers`),
        ]);
        setPreviewCategories(categoriesRes.data);
        setPreviewProducts(productsRes.data);
        setPreviewBestsellerIds(bestsellersRes.data.map((entry) => entry.product_id));
      } catch (error) {
        // Preview data is a nice-to-have -- section editing stays usable
        // even if this fails, sections just fall back to the empty state.
      } finally {
        setPreviewDataLoading(false);
      }
    })();
  }, [canEdit, orgId]);

  const productCollections = useMemo(
    () => deriveProductCollections(previewProducts, previewBestsellerIds),
    [previewProducts, previewBestsellerIds]
  );

  const sections = useMemo(() => homeSections(theme), [theme]);
  const selectedSection = sections.find((s) => s.id === selectedSectionId) || null;

  const patchSection = (sectionId, patch) => {
    setTheme((prev) => updateSectionsInDraft(prev, sectionId, (section) => ({ ...section, ...patch })));
    setDirty(true);
  };

  const patchSectionSettings = (sectionId, patch) => {
    setTheme((prev) => updateSectionsInDraft(prev, sectionId, (section) => ({ ...section, settings: { ...section.settings, ...patch } })));
    setDirty(true);
  };

  const setHomeSections = (newSections) => {
    setTheme((prev) => ({
      ...prev,
      draft: { ...prev.draft, pages: { ...prev.draft.pages, home: { ...prev.draft.pages.home, sections: newSections } } },
    }));
  };

  const reorderSections = (reorderedSections) => {
    const previousSections = sections;
    const reindexed = reorderedSections.map((section, index) => ({ ...section, position: index }));
    setHomeSections(reindexed);
    setDirty(true);
    setAnnouncement(`Volgorde bijgewerkt: ${reindexed.map((s) => SECTION_LABELS[s.type] || s.type).join(', ')}`);
    toast('Volgorde aangepast', {
      action: {
        label: 'Ongedaan maken',
        onClick: () => {
          setHomeSections(previousSections);
          setAnnouncement('Volgorde hersteld naar vorige staat');
        },
      },
    });
  };

  const addSection = (type) => {
    const newSection = {
      id: `section_${crypto.randomUUID()}`,
      type,
      enabled: true,
      position: sections.length,
      layout_variant: type === 'hero' ? 'minimal_no_image' : null,
      color_scheme: 'light',
      settings: SECTION_TYPE_DEFAULT_SETTINGS[type] || {},
    };
    setHomeSections([...sections, newSection]);
    setDirty(true);
    setSelectedSectionId(newSection.id);
    setAnnouncement(`${SECTION_LABELS[type] || type} toegevoegd`);
    toast.success(`${SECTION_LABELS[type] || type} toegevoegd — vergeet niet op te slaan`);
  };

  const removeSection = (id) => {
    const section = sections.find((s) => s.id === id);
    if (!section) return;
    if (!window.confirm(`Sectie "${SECTION_LABELS[section.type] || section.type}" verwijderen uit het concept?`)) return;
    const remaining = sections.filter((s) => s.id !== id).map((s, index) => ({ ...s, position: index }));
    setHomeSections(remaining);
    setDirty(true);
    if (selectedSectionId === id) setSelectedSectionId(remaining[0]?.id || null);
    setAnnouncement(`${SECTION_LABELS[section.type] || section.type} verwijderd`);
    toast.success('Sectie verwijderd — vergeet niet op te slaan');
  };

  const duplicateSection = (id) => {
    const index = sections.findIndex((s) => s.id === id);
    if (index === -1) return;
    const original = sections[index];
    const registryEntry = ADDABLE_SECTION_TYPES.find((t) => t.type === original.type);
    if (registryEntry?.maxCount === 1) {
      toast.error(`Er kan maar één ${SECTION_LABELS[original.type] || original.type}-sectie zijn`);
      return;
    }
    const copy = { ...original, id: `section_${crypto.randomUUID()}`, settings: { ...original.settings } };
    const newSections = [...sections.slice(0, index + 1), copy, ...sections.slice(index + 1)]
      .map((s, i) => ({ ...s, position: i }));
    setHomeSections(newSections);
    setDirty(true);
    setSelectedSectionId(copy.id);
    setAnnouncement(`${SECTION_LABELS[original.type] || original.type} gedupliceerd`);
    toast.success('Sectie gedupliceerd — vergeet niet op te slaan');
  };

  const patchGlobalSettings = (group, patch) => {
    setTheme((prev) => ({
      ...prev,
      draft: {
        ...prev.draft,
        settings: { ...prev.draft.settings, [group]: { ...(prev.draft.settings?.[group] || {}), ...patch } },
      },
    }));
    setDirty(true);
  };

  const applyPreset = (preset) => {
    setTheme((prev) => ({
      ...prev,
      draft: {
        ...prev.draft,
        settings: {
          ...prev.draft.settings,
          colors: preset.colors,
          typography: preset.typography,
          buttons: preset.buttons,
          cards: preset.cards,
        },
      },
    }));
    setDirty(true);
    toast.success(`${preset.label}-stijl toegepast — vergeet niet op te slaan`);
  };

  const reloadDraft = async () => {
    const res = await axios.get(`${API}/commerce/shops/${orgId}/theme/draft`);
    setTheme(res.data);
    setDirty(false);
  };

  const saveDraft = async () => {
    try {
      setSaving(true);
      const res = await axios.put(`${API}/commerce/shops/${orgId}/theme/draft`, theme.draft);
      setTheme(res.data);
      setDirty(false);
      toast.success('Concept opgeslagen');
      return true;
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error('Concept is ondertussen door iemand anders gewijzigd. Pagina wordt herladen.');
        await reloadDraft();
      } else {
        toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    try {
      setPublishing(true);
      const saveRes = await axios.put(`${API}/commerce/shops/${orgId}/theme/draft`, theme.draft);
      setTheme(saveRes.data);
      const publishRes = await axios.post(`${API}/commerce/shops/${orgId}/theme/publish`);
      setTheme(publishRes.data);
      setDirty(false);
      toast.success('Webshop-vormgeving gepubliceerd');
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error('Concept is ondertussen gewijzigd. Herlaad en probeer opnieuw.');
      } else {
        toast.error(error?.response?.data?.detail || 'Publiceren mislukt');
      }
    } finally {
      setPublishing(false);
    }
  };

  const discardChanges = async () => {
    if (!window.confirm('Alle niet-gepubliceerde wijzigingen in dit concept worden verwijderd en teruggezet naar de laatst gepubliceerde versie. Doorgaan?')) {
      return;
    }
    try {
      setDiscarding(true);
      const res = await axios.post(`${API}/commerce/shops/${orgId}/theme/discard`);
      setTheme(res.data);
      setDirty(false);
      toast.success('Wijzigingen verworpen — concept staat weer gelijk aan de live versie');
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error('Concept is ondertussen gewijzigd. Herlaad en probeer opnieuw.');
      } else {
        toast.error(error?.response?.data?.detail || 'Verwerpen mislukt');
      }
    } finally {
      setDiscarding(false);
    }
  };

  const openRevisions = async () => {
    setRevisionsOpen(true);
    try {
      setLoadingRevisions(true);
      const res = await axios.get(`${API}/commerce/shops/${orgId}/theme/revisions`);
      setRevisions(res.data);
    } catch (error) {
      toast.error('Kon revisiegeschiedenis niet laden');
    } finally {
      setLoadingRevisions(false);
    }
  };

  const restoreRevision = async (revisionId) => {
    try {
      setRestoringRevisionId(revisionId);
      const res = await axios.post(`${API}/commerce/shops/${orgId}/theme/revisions/${revisionId}/restore`);
      setTheme(res.data);
      setDirty(false);
      setRevisionsOpen(false);
      toast.success('Revisie teruggezet als concept — controleer en publiceer om live te zetten');
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error('Concept is ondertussen gewijzigd. Herlaad en probeer opnieuw.');
      } else {
        toast.error(error?.response?.data?.detail || 'Terugzetten mislukt');
      }
    } finally {
      setRestoringRevisionId(null);
    }
  };

  const sharePreviewLink = async () => {
    try {
      setSharingPreview(true);
      const res = await axios.post(`${API}/commerce/shops/${orgId}/theme/preview-token`);
      const url = `${window.location.origin}/${orgSlug}/shop?preview_token=${encodeURIComponent(res.data.token)}`;
      await navigator.clipboard.writeText(url);
      toast.success('Voorbeeldlink gekopieerd — 24 uur geldig, toont je concept aan wie de link heeft');
    } catch (error) {
      toast.error('Kon voorbeeldlink niet aanmaken');
    } finally {
      setSharingPreview(false);
    }
  };

  if (!canEdit) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-sm text-slate-500">
              Je hebt geen toegang tot de webshop-vormgeving.
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (loading || !theme) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[50vh]">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Negative margins cancel DashboardLayout's <main> padding. The 65px
          below is DashboardLayout's own fixed <header> (outside this
          component, measured, not guessed) -- the ONE place that budget is
          spent now. Below this, a flex-col shell makes the top bar a normal
          (non-sticky) row and everything else flex-1, so no other height
          math is needed regardless of how tall the top bar itself renders
          (previously three separate "100vh minus Npx" columns quietly broke
          when the bar wrapped, letting page scroll carry the whole bar
          off-screen). Each column below owns its own scrolling. */}
      <div className="-m-4 sm:-m-6 lg:-m-8 h-[calc(100vh-65px)] flex flex-col overflow-hidden">
        <div aria-live="polite" className="sr-only">{announcement}</div>
        {/* Top bar */}
        <div className="shrink-0 border-b border-slate-100 bg-white px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/commerce')} className="rounded-xl shrink-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-slate-900 truncate">Webshop-vormgeving</h1>
              <p className="text-xs text-slate-500 truncate">
                {dirty ? (
                  <span className="text-amber-600 font-medium">Niet-opgeslagen wijzigingen</span>
                ) : (
                  <span>Concept opgeslagen{timeAgo(theme.draft.updated_at) ? ` ${timeAgo(theme.draft.updated_at)}` : ''}{theme.draft.updated_by_name ? ` door ${theme.draft.updated_by_name}` : ''}</span>
                )}
                {' · '}
                {theme.published_at ? (
                  <span>live sinds {timeAgo(theme.published_at)}{theme.published_by_name ? ` (${theme.published_by_name})` : ''}</span>
                ) : (
                  <span>nog niet gepubliceerd</span>
                )}
              </p>
            </div>
          </div>
          <Tabs value={mode} onValueChange={setMode} className="hidden lg:block">
            <TabsList>
              <TabsTrigger value="sections">Secties</TabsTrigger>
              <TabsTrigger value="style">Huisstijl</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-xl">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem onClick={sharePreviewLink} disabled={sharingPreview}>
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Voorbeeldlink delen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openRevisions}>
                  <History className="w-4 h-4 mr-2" />
                  Revisiegeschiedenis
                </DropdownMenuItem>
                <DropdownMenuItem onClick={discardChanges} disabled={discarding || !theme.published_at} className="text-red-600">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Wijzigingen verwerpen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" className="rounded-xl" disabled={saving} onClick={saveDraft}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Opslaan als concept
            </Button>
            {canPublish && (
              <Button className="rounded-xl bg-teal-600 hover:bg-teal-700" disabled={publishing} onClick={publish}>
                {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Publiceren
              </Button>
            )}
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden lg:flex flex-1 min-h-0">
          {mode === 'sections' ? (
            <div className="w-64 shrink-0 border-r border-slate-100 bg-white p-4 overflow-y-auto overscroll-contain">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide px-1">Secties</p>
              <p className="text-xs text-slate-400 mb-2 px-1">Sleep het ⋮⋮-grepje om te herordenen</p>
              <SectionList
                sections={sections}
                selectedId={selectedSectionId}
                onSelect={setSelectedSectionId}
                onToggleEnabled={(id) => patchSection(id, { enabled: !sections.find((s) => s.id === id)?.enabled })}
                onReorder={reorderSections}
              />
              <AddSectionMenu sections={sections} onAdd={addSection} />
            </div>
          ) : null}
          <div className="flex-1 bg-slate-50 p-6 overflow-y-auto overscroll-contain">
            <LivePreview
              organizationName={organizationName}
              sections={sections}
              settings={theme.draft.settings}
              categories={previewCategories}
              productCollections={productCollections}
              previewDataLoading={previewDataLoading}
            />
          </div>
          <div className="w-96 shrink-0 border-l border-slate-100 bg-white p-4 overflow-y-auto overscroll-contain">
            {mode === 'sections' ? (
              selectedSection ? (
                <>
                  <div className="flex items-center justify-between gap-2 mb-4 px-1">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                      {SECTION_LABELS[selectedSection.type] || selectedSection.type}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" title="Dupliceren" onClick={() => duplicateSection(selectedSection.id)}>
                        <Copy className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" title="Verwijderen" onClick={() => removeSection(selectedSection.id)}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    </div>
                  </div>
                  <SectionSettingsForm
                    section={selectedSection}
                    organizationName={organizationName}
                    onPatchSection={(patch) => patchSection(selectedSection.id, patch)}
                    onPatchSettings={(patch) => patchSectionSettings(selectedSection.id, patch)}
                  />
                </>
              ) : (
                <p className="text-sm text-slate-400">Selecteer een sectie om te bewerken.</p>
              )
            ) : (
              <StyleSettingsForms settings={theme.draft.settings} onPatchGroup={patchGlobalSettings} onApplyPreset={applyPreset} />
            )}
          </div>
        </div>

        {/* Mobile: tabs + action bar, all within the fixed-viewport shell so
            the action bar is a normal last flex row, not sticky-positioned
            math against an assumed header height. */}
        <div className="lg:hidden flex-1 min-h-0 flex flex-col">
          <Tabs value={mode} onValueChange={setMode} className="flex-1 min-h-0 flex flex-col">
            <div className="shrink-0 border-b border-slate-100 bg-white px-4">
              <TabsList>
                <TabsTrigger value="sections">Secties</TabsTrigger>
                <TabsTrigger value="style">Huisstijl</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="sections" className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-1 mt-0">
              <p className="text-xs text-slate-400 mb-2 px-1">Sleep het ⋮⋮-grepje om te herordenen</p>
              <SectionList
                sections={sections}
                selectedId={selectedSectionId}
                onSelect={(id) => { setSelectedSectionId(id); setMobileSheetOpen(true); }}
                onToggleEnabled={(id) => patchSection(id, { enabled: !sections.find((s) => s.id === id)?.enabled })}
                onReorder={reorderSections}
              />
              <AddSectionMenu sections={sections} onAdd={(type) => { addSection(type); setMobileSheetOpen(true); }} />
            </TabsContent>
            <TabsContent value="style" className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 mt-0">
              <StyleSettingsForms settings={theme.draft.settings} onPatchGroup={patchGlobalSettings} onApplyPreset={applyPreset} />
            </TabsContent>
          </Tabs>

          {/* Mobile action bar */}
          <div
            className="shrink-0 border-t border-slate-100 bg-white px-4 py-3 flex items-center gap-2"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="rounded-xl shrink-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuItem onClick={sharePreviewLink} disabled={sharingPreview}>
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Voorbeeldlink delen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openRevisions}>
                  <History className="w-4 h-4 mr-2" />
                  Revisiegeschiedenis
                </DropdownMenuItem>
                <DropdownMenuItem onClick={discardChanges} disabled={discarding || !theme.published_at} className="text-red-600">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Wijzigingen verwerpen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" className="rounded-xl flex-1" onClick={() => setMobilePreviewOpen(true)}>
              <Eye className="w-4 h-4 mr-2" />
              Voorbeeld
            </Button>
            <Button variant="outline" className="rounded-xl flex-1" disabled={saving} onClick={saveDraft}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Opslaan
            </Button>
            {canPublish && (
              <Button className="rounded-xl bg-teal-600 hover:bg-teal-700 flex-1" disabled={publishing} onClick={publish}>
                Publiceren
              </Button>
            )}
          </div>
        </div>

        {/* Mobile section settings drawer */}
        <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
            <SheetHeader>
              <div className="flex items-center justify-between gap-2 pr-8">
                <SheetTitle>{selectedSection ? (SECTION_LABELS[selectedSection.type] || selectedSection.type) : ''}</SheetTitle>
                {selectedSection && (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" title="Dupliceren" onClick={() => duplicateSection(selectedSection.id)}>
                      <Copy className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" title="Verwijderen" onClick={() => { removeSection(selectedSection.id); setMobileSheetOpen(false); }}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                )}
              </div>
            </SheetHeader>
            {selectedSection && (
              <div className="mt-4">
                <SectionSettingsForm
                  section={selectedSection}
                  organizationName={organizationName}
                  onPatchSection={(patch) => patchSection(selectedSection.id, patch)}
                  onPatchSettings={(patch) => patchSectionSettings(selectedSection.id, patch)}
                />
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Mobile full-screen preview */}
        <Sheet open={mobilePreviewOpen} onOpenChange={setMobilePreviewOpen}>
          <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Voorbeeld</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <LivePreview
                organizationName={organizationName}
                sections={sections}
                settings={theme.draft.settings}
                categories={previewCategories}
                productCollections={productCollections}
                previewDataLoading={previewDataLoading}
              />
            </div>
          </SheetContent>
        </Sheet>

        {/* Revision history */}
        <Sheet open={revisionsOpen} onOpenChange={setRevisionsOpen}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Revisiegeschiedenis</SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-2">
              {loadingRevisions ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : revisions.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Nog geen publicaties — de geschiedenis begint bij je eerste keer publiceren.</p>
              ) : (
                revisions.map((revision) => (
                  <div key={revision.id} className="rounded-xl border border-slate-200 p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900">Revisie {revision.revision}</p>
                      <p className="text-xs text-slate-500 truncate">
                        Gepubliceerd {timeAgo(revision.published_at)}
                        {revision.published_by_name ? ` door ${revision.published_by_name}` : ''}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl shrink-0"
                      disabled={restoringRevisionId === revision.id}
                      onClick={() => restoreRevision(revision.id)}
                    >
                      {restoringRevisionId === revision.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Herstellen'}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
};

export default ThemeEditorPage;
